import { useState } from 'react';
import { DetailModal, BrainIcon, FormActions } from '@practium/ui';
import CalendarToday from '@mui/icons-material/CalendarToday';
import Info from '@mui/icons-material/Info';
import ContentCopy from '@mui/icons-material/ContentCopy';
import Check from '@mui/icons-material/Check';
import { LogStatus } from '../../../types/log.types';
import type { LogView } from '../../../types/log.types';
import styles from './LogDetailModal.module.css';

interface LogDetailModalProps {
	log: LogView;
	onClose: () => void;
	onDelete: () => void;
	isDeleting?: boolean;
}

const STATUS_COLOR: Record<LogStatus, string> = {
	[LogStatus.Info]: 'var(--primary)',
	[LogStatus.Warn]: 'var(--log-status-warning)',
	[LogStatus.Error]: 'var(--log-status-error)',
};

export const LogDetailModal = ({
	log,
	onClose,
	onDelete,
	isDeleting = false,
}: LogDetailModalProps) => {
	const [copied, setCopied] = useState(false);
	const iconColor = STATUS_COLOR[log.status];

	const handleCopy = () => {
		if (!log.stacktrace) return;
		navigator.clipboard.writeText(log.stacktrace).then(() => {
			setCopied(true);
			setTimeout(() => setCopied(false), 2000);
		});
	};

	const additionalFields = [
		...(log.usuario ? [{ label: 'Usuário', value: log.usuario }] : []),
		...(log.authorId ? [{ label: 'AuthorId', value: log.authorId }] : []),
		...(log.navegador ? [{ label: 'Navegador', value: log.navegador }] : []),
		...(log.stacktrace
			? [
					{
						label: 'Stacktrace',
						span: 'full' as const,
						value: (
							<div className={styles.stacktraceWrapper}>
								<pre className={styles.stacktrace}>{log.stacktrace}</pre>
								<button
									type="button"
									className={`${styles.copyBtn} ${copied ? styles.copyBtnCopied : ''}`}
									onClick={handleCopy}
									title={copied ? 'Copiado!' : 'Copiar stacktrace'}
								>
									{copied ? (
										<Check style={{ fontSize: '0.875rem' }} />
									) : (
										<ContentCopy style={{ fontSize: '0.875rem' }} />
									)}
								</button>
							</div>
						),
					},
				]
			: []),
	];

	return (
		<DetailModal
			title={`Log ${log.id} — ${log.statusLabel} no ${log.tipo}`}
			accentColor={log.status === LogStatus.Info ? undefined : iconColor}
			metaItems={[
				{
					icon: <CalendarToday className={styles.metaIcon} style={{ color: iconColor }} />,
					text: log.timestamp,
				},
				{
					icon: <Info className={styles.metaIcon} style={{ color: iconColor }} />,
					text: log.statusLabel,
				},
				{
					icon: <BrainIcon color={iconColor} />,
					text: log.tipo,
				},
			]}
			sections={[
				{
					title: 'Informações Gerais',
					fields: [
						{ label: 'Id', value: log.id },
						{ label: 'Categoria', value: log.statusLabel },
						{ label: 'Tipo', value: log.tipo },
						{ label: 'Timestamp', value: log.timestamp },
						{ label: 'Ação', value: log.acao, span: 'full' },
					],
				},
				{
					title: 'Informações Adicionais',
					fields: additionalFields,
				},
			]}
			footer={<FormActions mode="view" onDelete={isDeleting ? undefined : onDelete} />}
			onClose={onClose}
		/>
	);
};
